
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { CostMultiplierForm, CostSettings } from 'components/domain/settings';
import {
  addNotification,
  fetchTravelCosts,
  selectDistanceCalculationNeeded,
  selectDurationCalculationNeeded,
} from 'store/reducers/inputReducer.js';
import { selectCostSetting, selectTableLength } from 'store/reducers/selectors';


function TabPanel({ children, value, tabId }) {
  if (value !== tabId) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.string.isRequired,
  tabId: PropTypes.string.isRequired,
};

export default function CostSettingsDialog(props) {
  const { open, onClose } = props;

  const [activeTab, setActiveTab] = useState('cost');

  const dispatch = useDispatch();

  const costMetric = useSelector(selectCostSetting({ settingName: 'costMetric' }));
  const durationCalculationNeeded = useSelector(selectDurationCalculationNeeded);
  const distanceCalculationNeeded = useSelector(selectDistanceCalculationNeeded);
  const locationsTableLength = useSelector(selectTableLength('locations'));

  const handleClose = () => {
    if (typeof onClose === 'function') onClose();
  };

  const handleCalculate = (event) => {
    event?.stopPropagation?.();

    if (!locationsTableLength || locationsTableLength === 0) {
      dispatch(
        addNotification({
          notificationMsg: 'There are no locations to compute travel costs for',
          notificationType: 'warning',
        })
      );
      return;
    }

    dispatch(fetchTravelCosts());
    handleClose();
  };

  const calculateAvailable = costMetric !== 'custom';

  return (
    <Dialog
      aria-labelledby="cost-settings-dialog-title"
      open={Boolean(open)}
      onClose={handleClose}
      onClick={(event) => event.stopPropagation()}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 20px 60px rgba(23, 43, 77, 0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id="cost-settings-dialog-title"
        sx={{
          px: 2,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          background:
            'linear-gradient(180deg, rgba(230, 252, 251, 0.55) 0%, rgba(255, 255, 255, 1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.extraLight',
              color: 'primary.main',
              boxShadow: '0 8px 20px rgba(0, 146, 139, 0.12)',
              flexShrink: 0,
            }}
          >
            <TuneRoundedIcon sx={{ fontSize: '1.15rem' }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: 'dark.main', lineHeight: 1.15 }}
              noWrap
            >
              Cost settings
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.15 }}
              noWrap
            >
              Optimize by distance, duration, or enter costs manually
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 146, 139, 0.08)',
              color: 'primary.main',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2.25 }}>
        <Tabs
          value={activeTab}
          onChange={(_event, next) => setActiveTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 2,
              mr: 1,
            },
            '& .MuiTab-root.Mui-selected': {
              color: 'primary.main',
              backgroundColor: 'primary.extraLight',
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 2,
            },
          }}
        >
          <Tab value="cost" label="Cost" />
          <Tab value="distance" label="Distance" disabled={!distanceCalculationNeeded} />
          <Tab value="duration" label="Duration" disabled={!durationCalculationNeeded} />
        </Tabs>

        <TabPanel value={activeTab} tabId="cost">
          <CostMultiplierForm />
        </TabPanel>
        <TabPanel value={activeTab} tabId="distance">
          <CostSettings costMetric="distance" radioLabel="Distance calculation method" />
        </TabPanel>
        <TabPanel value={activeTab} tabId="duration">
          <CostSettings costMetric="duration" radioLabel="Duration calculation method" />
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1, justifyContent: 'flex-end' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: 'primary.main',
            color: 'primary.main',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: 'primary.extraDark', backgroundColor: 'primary.extraLight' },
          }}
        >
          Done
        </Button>

        {calculateAvailable && (
          <Button
            onClick={handleCalculate}
            variant="contained"
            sx={{
              color: '#fff',
              backgroundColor: 'primary.main',
              borderColor: 'primary.main',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: 'primary.extraDark', backgroundColor: 'primary.extraDark' },
            }}
          >
            Done & Calculate
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

CostSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};
